import React, { useState } from 'react'

import {
  Button,
  ButtonGroup,
  Checkbox,
  Fieldset,
  Popper,
  RadioButton,
  useMediaQueryContext,
} from '@fourthwall/components'
import { ArrowShortDownIcon } from '@fourthwall/icons'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { isTimeFrameFrom, isTimeFrameNone, isTimeFrameRange, isTimeFrameTo } from '@utils/timeframe'

import { TimezonedDatePicker } from '../../../modules/Timezone/components/TimezonedDatePicker'
import { StatusTag } from '../../common'
import { validationSchema } from './consts'
import * as S from './styled'
import type { FormValues, StatusTagDropdownProps } from './types'
import { FormFields } from './types'

export const StatusTagDropdown = ({
  initialIsOpen = false,
  headerContent,
  status,
  timeFrame,
  isSoldOut,
  disabled = false,
  size = 'large',
  onSubmit,
}: StatusTagDropdownProps) => {
  const media = useMediaQueryContext()
  const [isOpen, setOpen] = useState(initialIsOpen && !disabled)

  const getInitialValues = (): FormValues => {
    const getStatusFormValue = () => {
      if (timeFrame && !isTimeFrameNone(timeFrame)) {
        return 'SCHEDULE'
      }

      return status
    }

    let startDate
    let endDate
    let setEndDate = false

    if (timeFrame) {
      startDate =
        isTimeFrameFrom(timeFrame) || isTimeFrameRange(timeFrame)
          ? new Date(timeFrame.from)
          : undefined
      endDate =
        isTimeFrameTo(timeFrame) || isTimeFrameRange(timeFrame) ? new Date(timeFrame.to) : undefined
      setEndDate = !!endDate
    }

    return {
      status: getStatusFormValue(),
      startDate,
      setEndDate,
      endDate,
    }
  }

  const formik = useFormik<FormValues>({
    initialValues: getInitialValues(),
    onSubmit: async (values) => {
      await onSubmit(values)
      setOpen(false)
    },
    validationSchema,
    enableReinitialize: true,
  })

  const toggleOpen = () => {
    if (disabled) return

    setOpen((prevIsOpen) => !prevIsOpen)
  }

  const renderTimeFrameRangeInput = () => (
    <>
      <Fieldset.Row>
        <RadioButton
          value={formik.values.status}
          checkedValue="SCHEDULE"
          label="Schedule as public"
          suggestion="Choose a date to make product go public."
          name="status"
          onChange={formik.handleChange}
        />
      </Fieldset.Row>
      {formik.values.status === 'SCHEDULE' && (
        <>
          <Fieldset.Row inner>
            <TimezonedDatePicker
              value={[formik.values.startDate]}
              labels={['Launch date']}
              showTimeInputs
              minDate={new Date()}
              onChange={([newStartDate]) => {
                formik.setFieldValue(FormFields.startDate, newStartDate)
              }}
            />
          </Fieldset.Row>
          <Fieldset.Row inner>
            <Checkbox
              label="Set end date"
              value={formik.values.setEndDate}
              name="setEndDate"
              onChange={formik.handleChange}
            />
          </Fieldset.Row>
          {formik.values.setEndDate && (
            <Fieldset.Row inner>
              <TimezonedDatePicker
                value={[formik.values.endDate]}
                labels={['End date']}
                minDate={formik.values.startDate || new Date()}
                showTimeInputs
                onChange={([newEndDate]) => {
                  formik.setFieldValue(FormFields.endDate, newEndDate)
                }}
              />
            </Fieldset.Row>
          )}
        </>
      )}
    </>
  )

  const renderForm = () => (
    <S.FormContainer>
      {headerContent}
      <form onSubmit={formik.handleSubmit}>
        <Fieldset>
          <Fieldset.Row>
            <RadioButton
              value={formik.values.status}
              checkedValue="PUBLIC"
              label="Public"
              suggestion="Everyone has access."
              name="status"
              onChange={formik.handleChange}
            />
          </Fieldset.Row>
          <Fieldset.Row>
            <RadioButton
              value={formik.values.status}
              checkedValue="HIDDEN"
              label="Hidden"
              suggestion="Everyone with link has access."
              name="status"
              onChange={formik.handleChange}
            />
          </Fieldset.Row>
          {timeFrame && renderTimeFrameRangeInput()}
        </Fieldset>
        <S.ButtonsContainer>
          <ButtonGroup
            space="medium"
            justify={media.tablet ? 'right' : undefined}
            direction={media.tablet ? 'row' : 'column-reverse'}
            fullWidth={!media.tablet}
          >
            <Button
              label="Cancel"
              size={media.tablet ? 'small' : 'medium'}
              fullWidth={!media.tablet}
              onClick={() => {
                formik.resetForm()
                setOpen(false)
              }}
            />
            <Button
              label="Save"
              appearance="primary"
              size={media.tablet ? 'small' : 'medium'}
              fullWidth={!media.tablet}
              disabled={!formik.dirty || !formik.isValid}
              loading={formik.isSubmitting}
              onClick={formik.submitForm}
            />
          </ButtonGroup>
        </S.ButtonsContainer>
      </form>
    </S.FormContainer>
  )

  return (
    <Popper
      isOpen={isOpen}
      referenceComponent={
        <button type="button" onClick={toggleOpen}>
          <StatusTag
            status={status}
            timeFrame={timeFrame}
            size={size}
            icon={!disabled ? ArrowShortDownIcon : undefined}
            isSoldOut={isSoldOut}
          />
        </button>
      }
      popperProps={{
        placement: 'bottom-end',
        modifiers: [
          { name: 'preventOverflow', enabled: false },
          { name: 'hide', enabled: false },
        ],
      }}
      style={{ paddingTop: media.tablet ? 8 : 0 }}
      onClose={() => setOpen(false)}
    >
      {renderForm()}
    </Popper>
  )
}
